import { CHANNEL_LABELS, CHANNELS, FORMAT_LABELS, SOURCE_LABELS, STAGES } from '@jt/core';
import type { ApplicationFull, Parsed, ParseResult } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, post } from '../api/client';
import { applyFull, keys, refreshSummaries, useApplications } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon } from '../components/ui';

const errText = (e: unknown) =>
  e instanceof ApiError
    ? (Object.values(e.fields)[0] ?? e.message)
    : 'Не получилось. Повторите через пару секунд.';

/** «Вставить текст»: вставка → разбор Claude → предпросмотр с правкой → сохранение. */
export function PasteForm({
  targetId,
  submit,
}: {
  targetId: string | null;
  /** Своё сохранение (сообщение LinkedIn из «Входящих» — сразу отметить разобранным). */
  submit?: (body: Record<string, unknown>) => Promise<ApplicationFull>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState<ParseResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const run = async () => {
    if (!text.trim()) return setStatus('Сначала вставьте текст.');
    setBusy(true);
    setStatus('Claude читает текст…');
    setResult(null);
    try {
      const r = await post<ParseResult>('/ai/parse', { text, targetApplicationId: targetId });
      setResult(r);
      setStatus(
        r.truncated ? 'Текст длинный — разобрано начало. Проверьте и сохраните.' : 'Проверьте и сохраните.',
      );
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) {
      setStatus(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="sec">
        <label className="f">
          Вакансия из Telegram или LinkedIn, письмо рекрутера, сообщение HR, уведомление об отказе
          <textarea
            rows={9}
            placeholder="Вставьте текст целиком"
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
        </label>
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={run} disabled={busy}>
            <Icon name="sparkles" size={16} /> {busy ? 'Разбираю…' : 'Разобрать'}
          </button>
        </div>
        <p className="muted sm" role="status">
          {status}
        </p>
      </section>
      <div ref={resultRef}>
        {result && (
          <ParsedPreview
            key={result.raw + result.parsed.company}
            r={result}
            targetId={targetId}
            submit={submit}
          />
        )}
      </div>
    </>
  );
}

/** «По ссылке»: сервер открывает страницу вакансии → разбор Claude → тот же предпросмотр. */
export function UrlForm({ targetId }: { targetId: string | null }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState<ParseResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const run = async (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setStatus('Сначала вставьте ссылку.');
    setBusy(true);
    setStatus('Открываю страницу и читаю вакансию…');
    setResult(null);
    try {
      const r = await post<ParseResult>('/ai/parse-url', { url: url.trim(), targetApplicationId: targetId });
      setResult(r);
      setStatus(
        r.truncated
          ? 'Страница длинная — разобрано начало. Проверьте и сохраните.'
          : 'Проверьте и сохраните.',
      );
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (x) {
      setStatus(errText(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form className="sec" onSubmit={run}>
        <label className="f">
          Ссылка на вакансию: LinkedIn, сайт компании, доска вакансий, пост в Telegram
          <input
            type="url"
            inputMode="url"
            placeholder="https://"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
          />
        </label>
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button className="btn primary" type="submit" disabled={busy}>
            <Icon name="sparkles" size={16} /> {busy ? 'Читаю страницу…' : 'Собрать'}
          </button>
        </div>
        <p className="muted sm" role="status">
          {status}
        </p>
      </form>
      <div ref={resultRef}>
        {result && <ParsedPreview key={result.raw + result.parsed.company} r={result} targetId={targetId} />}
      </div>
    </>
  );
}

function Field({ label, children, full }: { label: string; children: ReactNode; full?: boolean }) {
  return (
    <label className={`f ${full ? 'full' : ''}`}>
      {label}
      {children}
    </label>
  );
}

export function ParsedPreview({
  r,
  targetId,
  onSaved,
  extra,
  submit,
}: {
  r: ParseResult;
  targetId: string | null;
  /** После сохранения (например, отметить письмо разобранным). */
  onSaved?: (full: ApplicationFull) => void | Promise<void>;
  extra?: { occurredOn?: string };
  /** Своё сохранение (например, «Входящие» → POST /inbox/:id/apply с правками). */
  submit?: (body: Record<string, unknown>) => Promise<ApplicationFull>;
}) {
  const ui = useUI();
  const qc = useQueryClient();
  const apps = useApplications();
  const p = r.parsed;
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const defaultTarget = targetId ?? p.matchId ?? 'new';
  const [target, setTarget] = useState(defaultTarget);
  const sorted = [...(apps.data ?? [])].sort((a, b) => a.company.localeCompare(b.company));
  const defaultStage = p.stage ?? (target === 'new' && p.type === 'vacancy' ? 'applied' : '');
  const o = p.offer;

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim();
    const orNull = (k: string) => v(k) || null;
    const body = {
      target,
      raw: r.raw,
      ...(extra?.occurredOn ? { occurredOn: extra.occurredOn } : {}),
      company: v('company'),
      role: v('role'),
      url: v('url'),
      source: v('source') as Parsed['source'],
      location: v('location'),
      format: orNull('format'),
      salary: v('salary'),
      contact: {
        name: v('cname'),
        role: v('crole'),
        email: v('cemail'),
        linkedinUrl: v('clinkedin'),
        telegram: v('ctelegram'),
        phone: v('cphone'),
      },
      ...(r.description !== undefined ? { notes: v('notes') } : {}),
      channel: v('channel'),
      direction: v('dir'),
      summary: v('summary'),
      stage: orNull('stage'),
      rejectionReason: v('reason'),
      nextStep: v('next'),
      nextDate: orNull('nextDate'),
      nextTime: orNull('nextTime'),
      offer: o
        ? {
            salaryK: v('osalary') ? Number(v('osalary').replace(',', '.')) || null : null,
            bonus: v('obonus'),
            format: orNull('oformat'),
            contract: v('ocontract'),
            startDate: orNull('ostart'),
            deadline: orNull('odeadline'),
            benefits: v('obenefits'),
          }
        : null,
    };
    setBusy(true);
    try {
      const full = submit ? await submit(body) : await post<ApplicationFull>('/ai/parse/apply', body);
      applyFull(qc, full);
      void qc.invalidateQueries({ queryKey: keys.apps() });
      refreshSummaries(qc);
      await onSaved?.(full);
      ui.closeSheet();
      ui.openApp(full.id);
      ui.toast('Сохранено');
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };

  const input = (
    name: string,
    label: string,
    value: string | number | null | undefined,
    type = 'text',
    full = false,
  ) => (
    <Field label={label} full={full}>
      <input name={name} type={type} defaultValue={value ?? ''} />
    </Field>
  );

  return (
    <form onSubmit={save}>
      <section className="sec">
        <h3>Куда сохранить</h3>
        <Field label="Вакансия">
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="new">Новая вакансия</option>
            {sorted.map((a) => (
              <option key={a.id} value={a.id}>
                {a.company || 'Без названия'} — {a.role}
              </option>
            ))}
          </select>
        </Field>
        {p.matchId && !targetId && (
          <p className="muted sm">Похоже на вакансию, которая уже есть в трекере.</p>
        )}
      </section>
      <section className="sec">
        <h3>Вакансия</h3>
        <div className="grid">
          {input('company', 'Компания', p.company)}
          {input('role', 'Роль', p.role)}
          <Field label="Источник">
            <select name="source" defaultValue={p.source}>
              {Object.entries(SOURCE_LABELS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          {input('location', 'Город / страна', p.location)}
          <Field label="Формат">
            <select name="format" defaultValue={p.format ?? ''}>
              <option value="">—</option>
              {Object.entries(FORMAT_LABELS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          {input('salary', 'Вилка', p.salary)}
          {input('url', 'Ссылка', p.url, 'url', true)}
          {r.description !== undefined && (
            <Field label="Описание вакансии (в заметки)" full>
              <textarea name="notes" rows={6} defaultValue={r.description} />
            </Field>
          )}
        </div>
      </section>
      <section className="sec">
        <h3>Контакт</h3>
        <div className="grid">
          {input('cname', 'Имя', p.contact.name)}
          {input('crole', 'Кто это', p.contact.role)}
          {input('cemail', 'Email', p.contact.email, 'email')}
          {input('clinkedin', 'LinkedIn', p.contact.linkedinUrl)}
          {input('ctelegram', 'Telegram', p.contact.telegram)}
          {input('cphone', 'Телефон', p.contact.phone, 'tel')}
        </div>
      </section>
      <section className="sec">
        <h3>Запись в историю</h3>
        <div className="grid">
          <Field label="Канал">
            <select name="channel" defaultValue={p.channel}>
              {CHANNELS.filter((c) => c !== 'System').map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABELS[c]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Кто написал">
            <select name="dir" defaultValue={p.direction}>
              <option value="in">Мне написали</option>
              <option value="out">Я написал</option>
              <option value="sys">Автоматическое уведомление</option>
            </select>
          </Field>
          <Field label="Кратко" full>
            <textarea name="summary" rows={3} defaultValue={p.summary} />
          </Field>
          <Field label="Новый статус">
            <select name="stage" defaultValue={defaultStage}>
              <option value="">Не менять</option>
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.title}
                </option>
              ))}
            </select>
          </Field>
          {input('reason', 'Причина отказа', p.rejectionReason)}
          {input('next', 'Следующий шаг', p.nextStep)}
          {input('nextDate', 'Когда', p.nextDate, 'date')}
          {input('nextTime', 'Время', p.nextTime, 'time')}
        </div>
      </section>
      {o && (
        <section className="sec">
          <h3>Оффер</h3>
          <div className="grid">
            {input('osalary', 'Зарплата, тыс. €/год', o.salaryK)}
            {input('obonus', 'Бонус', o.bonus)}
            <Field label="Формат">
              <select name="oformat" defaultValue={o.format ?? ''}>
                <option value="">—</option>
                {Object.entries(FORMAT_LABELS).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            {input('ocontract', 'Контракт', o.contract)}
            {input('ostart', 'Выход', o.startDate, 'date')}
            {input('odeadline', 'Ответить до', o.deadline, 'date')}
            {input('obenefits', 'Условия', o.benefits, 'text', true)}
          </div>
        </section>
      )}
      {err && (
        <p className="fnote err" role="alert">
          {err}
        </p>
      )}
      <button className="btn primary" type="submit" disabled={busy}>
        Сохранить
      </button>
    </form>
  );
}
